import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import LinkedText from '../LinkedText';

const PaperModal = ({ paperData }) => {
    const navigate = useNavigate();
    const modalRef = useRef(null);
    const [selectedImageIndex, setSelectedImageIndex] = useState(null);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [previewPdf, setPreviewPdf] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);

    useEffect(() => {
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    useEffect(() => {
        if (isLightboxOpen || previewPdf) {
            if (modalRef.current) {
                modalRef.current.style.overflow = 'hidden';
            }
        } else if (modalRef.current) {
            modalRef.current.style.overflow = 'auto';
        }
    }, [isLightboxOpen, previewPdf]);

    const handleClose = useCallback(() => {
        navigate('/');
    }, [navigate]);

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    const closeLightbox = () => {
        setIsLightboxOpen(false);
        setSelectedImageIndex(null);
    };

    const navigateImage = useCallback((direction) => {
        if (!paperData.gallery) return;

        const mediaItems = paperData.gallery;
        const totalItems = mediaItems.length;
        if (direction === 'next') {
            setSelectedImageIndex((prev) => (prev + 1) % totalItems);
        } else {
            setSelectedImageIndex((prev) => (prev - 1 + totalItems) % totalItems);
        }
    }, [paperData.gallery]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                if (isLightboxOpen) {
                    closeLightbox();
                } else if (previewPdf) {
                    setPreviewPdf(null);
                    setPdfLoading(false);
                } else {
                    handleClose();
                }
            }
            if (isLightboxOpen) {
                if (e.key === 'ArrowLeft') {
                    navigateImage('prev');
                } else if (e.key === 'ArrowRight') {
                    navigateImage('next');
                }
            }
        };

        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isLightboxOpen, selectedImageIndex, previewPdf, handleClose, navigateImage]);

    if (!paperData) return null;

    // Helper to find the main PDF document
    const mainPdf = paperData.documents?.find(doc => doc.type === 'pdf' || doc.src.endsWith('.pdf'));

    return (
        <>
            <div
                className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/90 backdrop-blur-sm animate-fade-in"
                onClick={handleBackdropClick}
            >
                <div
                    ref={modalRef}
                    className="relative w-full max-w-6xl bg-[#0a0a0a] my-4 md:my-8 rounded-xl shadow-2xl animate-slide-up overflow-hidden border border-gray-800"
                >
                    <button
                        onClick={handleClose}
                        className="absolute top-4 right-4 z-50 w-10 h-10 flex items-center justify-center bg-black/50 hover:bg-black/80 rounded-full transition-colors border border-white/10"
                        aria-label="Close modal"
                    >
                        <svg className="w-5 h-5 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Compact Hero Section */}
                    <div className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
                        <img
                            src={paperData.heroImage}
                            alt={`${paperData.name} hero`}
                            className="w-full h-full object-cover opacity-60"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-transparent" />

                        <div className="absolute bottom-0 left-0 w-full p-8 md:p-12">
                            <div className="container mx-auto">
                                <div className="flex flex-wrap gap-2 mb-4">
                                    <span className="bg-red-600/90 text-white text-xs font-bold px-3 py-1 rounded tracking-wider uppercase">
                                        {paperData.category}
                                    </span>
                                    <span className="bg-white/10 backdrop-blur-md text-white border border-white/20 text-xs font-semibold px-3 py-1 rounded">
                                        {paperData.year}
                                    </span>
                                </div>
                                <h1 className="text-3xl md:text-5xl font-black text-white mb-2 tracking-tight leading-tight">
                                    {paperData.name}
                                </h1>
                                <p className="text-lg md:text-xl text-gray-300 font-light italic max-w-3xl">
                                    {paperData.tagline}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Main Content Areas */}
                    <div className="px-8 pb-8 pt-2 md:px-12 md:pb-12 md:pt-4 bg-[#0a0a0a]">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

                            {/* Left Column: Abstract & Academic Content */}
                            <div className="lg:col-span-2 space-y-10">
                                <section>
                                    <h2 className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-4 border-b border-gray-800 pb-2">
                                        Abstract
                                    </h2>
                                    <p className="text-lg md:text-xl text-gray-300 leading-relaxed font-serif text-justify text-justify-last-left">
                                        <LinkedText text={paperData.description} />
                                    </p>

                                    {paperData.highlight && (
                                        <div className="mt-8 relative pl-6 border-l-2 border-red-600">
                                            <p className="text-xl font-serif text-white italic leading-relaxed">
                                                "{paperData.highlight}"
                                            </p>
                                        </div>
                                    )}
                                </section>

                                {paperData.gallery && paperData.gallery.length > 0 && (
                                    <section>
                                        <h2 className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-6 border-b border-gray-800 pb-2">
                                            Key Figures & Visualization
                                        </h2>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            {paperData.gallery.map((item, index) => (
                                                <div
                                                    key={index}
                                                    className="group relative aspect-video bg-gray-900 rounded border border-gray-800 overflow-hidden cursor-pointer hover:border-gray-600 transition-all"
                                                    onClick={() => {
                                                        setSelectedImageIndex(index);
                                                        setIsLightboxOpen(true);
                                                    }}
                                                >
                                                    {item.type === 'image' ? (
                                                        <img
                                                            src={item.src}
                                                            alt={item.alt}
                                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                        />
                                                    ) : null}
                                                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-black/80 backdrop-blur-sm translate-y-full group-hover:translate-y-0 transition-transform">
                                                        <p className="text-xs text-white truncate">{item.alt || `Figure ${index + 1}`}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                )}
                            </div>

                            {/* Right Column: Information Card & Actions */}
                            <div className="lg:col-span-1 space-y-8">

                                {/* Paper Actions Card */}
                                <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 sticky top-8">
                                    <h3 className="text-white font-bold text-lg mb-6 flex items-center gap-2">
                                        <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        Paper Resources
                                    </h3>

                                    <div className="space-y-4">
                                        {mainPdf ? (
                                            <>
                                                <button
                                                    onClick={() => {
                                                        setPdfLoading(true);
                                                        setPreviewPdf(mainPdf);
                                                    }}
                                                    className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-lg transition-all shadow-lg shadow-red-900/20 group"
                                                >
                                                    <span>Read Full Paper</span>
                                                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                                    </svg>
                                                </button>
                                                <a
                                                    href={mainPdf.src}
                                                    download
                                                    className="w-full flex items-center justify-center gap-2 bg-white text-black hover:bg-gray-200 font-bold py-3 px-4 rounded-lg transition-all"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                    </svg>
                                                    Download PDF
                                                </a>
                                            </>
                                        ) : (
                                            paperData.documents?.map((doc, idx) => (
                                                <a
                                                    key={idx}
                                                    href={doc.src}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-lg transition-all shadow-lg shadow-red-900/20 group"
                                                >
                                                    <span>{doc.title || 'View Resource'}</span>
                                                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                    </svg>
                                                </a>
                                            ))
                                        )}
                                    </div>


                                    {paperData.techStack && (
                                        <div className="mt-8 pt-8 border-t border-gray-800">
                                            <h4 className="text-gray-400 text-sm font-bold uppercase tracking-wider mb-4">Methodology & Tools</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {paperData.techStack.map((tech, index) => (
                                                    <span
                                                        key={index}
                                                        className="px-3 py-1 bg-gray-800 text-gray-200 rounded text-xs font-medium border border-gray-700"
                                                    >
                                                        {tech}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Lightbox for Gallery */}
            {isLightboxOpen && selectedImageIndex !== null && paperData.gallery && (
                <div
                    className="fixed inset-0 z-[100] bg-black/98 flex items-center justify-center animate-fade-in"
                    onClick={closeLightbox}
                >
                    <button
                        onClick={closeLightbox}
                        className="absolute top-4 right-4 z-50 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                    >
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    <div
                        className="relative w-full h-full flex items-center justify-center p-4 lg:p-12"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={paperData.gallery[selectedImageIndex].src}
                            alt={paperData.gallery[selectedImageIndex].alt}
                            className="max-w-full max-h-full object-contain rounded-sm shadow-2xl"
                        />
                        <div className="absolute bottom-8 text-white bg-black/50 px-6 py-2 rounded-full font-medium backdrop-blur-md">
                            Figure {selectedImageIndex + 1}: {paperData.gallery[selectedImageIndex].alt}
                        </div>
                    </div>
                </div>
            )}

            {/* PDF Preview Modal */}
            {previewPdf && (
                <div
                    className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center animate-fade-in p-0 md:p-8"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setPreviewPdf(null);
                            setPdfLoading(false);
                        }
                    }}
                >
                    <button
                        onClick={() => {
                            setPreviewPdf(null);
                            setPdfLoading(false);
                        }}
                        className="absolute top-4 right-4 z-[110] w-12 h-12 flex items-center justify-center bg-white text-black hover:bg-gray-200 rounded-full transition-colors shadow-lg"
                        aria-label="Close preview"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    <div
                        className="relative w-full h-full max-w-6xl bg-gray-900 rounded-lg shadow-2xl overflow-hidden flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-gray-800 px-4 py-3 flex justify-between items-center border-b border-gray-700">
                            <h3 className="text-white font-medium truncate pr-8">{previewPdf.title || 'PDF Viewer'}</h3>
                            <a
                                href={previewPdf.src}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 hover:text-white text-sm flex items-center gap-1 transition-colors"
                            >
                                Open in New Tab
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                            </a>
                        </div>

                        <div className="flex-1 relative bg-gray-200">
                            {pdfLoading && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 z-10 text-black">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mb-2"></div>
                                    <div className="text-sm font-medium">Loading Document...</div>
                                </div>
                            )}
                            <iframe
                                src={previewPdf.src}
                                title={previewPdf.title || 'PDF Preview'}
                                className="w-full h-full"
                                onLoad={() => setPdfLoading(false)}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PaperModal;
